import React from 'react';
import Modal from '../../common/Modal/Modal';
import LocationSelector from '../../common/Forms/LocationSelector';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import { partners } from '../Partners/PartnersSection';
import './AddEventModal.css';

const AddEventModal = ({ 
    isOpen, 
    onClose, 
    newEvent, 
    setNewEvent,
    handleAddEvent, 
    isLoading, 
    error,
    message,
    eventImageFile,
    previewEventImage,
    handleEventFileChange,
    handleRemoveEventImage
}) => {
    if (!isOpen) {
        return null;
    }

    // Generic input change handler for this form
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewEvent(prev => ({ ...prev, [name]: value }));
    };

    const handlePartnerChange = (e) => {
        const selectedPartners = Array.from(e.target.selectedOptions, option => option.value);
        setNewEvent(prev => ({
            ...prev,
            partners: selectedPartners
        }));
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add New Event">
            <form 
                className="form-container add-event-modal__form"
                onSubmit={handleAddEvent}
            >
                {error && <ErrorMessage message={error} />}
                {message && <div className="success-message">{message}</div>}
                {isLoading && <LoadingSpinner />}
                
                <div className="form-group">
                    <label htmlFor="eventTitleModal" className="form-label">Title *</label>
                    <input
                        type="text"
                        id="eventTitleModal"
                        name="title"
                        className="form-input"
                        value={newEvent.title}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="eventDescriptionModal" className="form-label">Description</label>
                    <textarea
                        id="eventDescriptionModal"
                        name="description"
                        className="form-textarea"
                        value={newEvent.description}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="eventDateModal" className="form-label">Date</label>
                    <input
                        type="date"
                        id="eventDateModal"
                        name="date"
                        className="form-input"
                        value={newEvent.date}
                        onChange={handleInputChange}
                        required
                        disabled={isLoading}
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">Location</label>
                    <LocationSelector
                        countryCode={newEvent.country}
                        city={newEvent.city}
                        onCountryChange={(code) => {
                            setNewEvent(prev => ({ ...prev, country: code, city: '' }));
                        }}
                        onCityChange={(cityName) => {
                            setNewEvent(prev => ({ ...prev, city: cityName }));
                        }}
                        required={true}
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="eventRequirementsModal" className="form-label">Requirements *</label>
                    <textarea
                        id="eventRequirementsModal"
                        name="requirements"
                        className="form-textarea"
                        value={newEvent.requirements}
                        onChange={handleInputChange}
                        placeholder="Enter requirements for participants"
                        disabled={isLoading}
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="eventRegistrationLinkModal" className="form-label">Registration Link *</label>
                    <input
                        type="url"
                        id="eventRegistrationLinkModal"
                        name="registrationLink"
                        className="form-input"
                        value={newEvent.registrationLink}
                        onChange={handleInputChange}
                        placeholder="https://example.com/register"
                        disabled={isLoading}
                        required
                    />
                </div>
                
                <div className="form-group add-event-modal__image-upload-group">
                    <label className="form-label">Event Image *</label>
                    <input
                        type="file"
                        id="eventImageModalInput"
                        accept="image/*"
                        onChange={handleEventFileChange}
                        className="form-file-input"
                        disabled={isLoading}
                        required
                    />
                    <label htmlFor="eventImageModalInput" className="form-file-label add-event-modal__image-upload-label">
                        <i className="fas fa-upload"></i> Choose Image
                    </label>
                    {previewEventImage && (
                        <div className="form-file-preview add-event-modal__image-preview">
                            <div className="add-event-modal__preview-item">
                                <img src={previewEventImage} alt="Event Preview" className="add-event-modal__preview-img"/>
                                <button
                                    type="button"
                                    className="add-event-modal__remove-image"
                                    onClick={handleRemoveEventImage}
                                    disabled={isLoading}
                                >
                                    &times;
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label className="form-label">Partners</label>
                    <div className="partners-checkbox-list">
                        {partners.map((partner, index) => (
                            <label key={index} className="partner-checkbox-item">
                                <input
                                    type="checkbox"
                                    value={partner.name}
                                    checked={newEvent.partners?.includes(partner.name)}
                                    onChange={(e) => {
                                        const partnerName = e.target.value;
                                        setNewEvent(prev => ({
                                            ...prev,
                                            partners: e.target.checked
                                                ? [...(prev.partners || []), partnerName]
                                                : (prev.partners || []).filter(p => p !== partnerName)
                                        }));
                                    }}
                                />
                                <span>{partner.name}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="form-actions">
                    <button 
                        type="button" 
                        onClick={onClose}
                        disabled={isLoading}
                        className="form-button"
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        disabled={isLoading}
                        className="form-submit"
                    >
                        {isLoading ? 'Processing...' : 'Add Event'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default AddEventModal; 